import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, HEALTH_COLORS, R, font } from '../theme/tokens';
import { progress, tasksOf } from '../state/selectors';
import { HELP_ROLES } from '../data/seed';
import { campaignUrl, captionWithTags, copyText, nativeShare } from '../services/share';
import { photoUrl } from '../data/photos';
import { dayLabel, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import type { Health, PhotoKind } from '../domain/types';
import { HOME_THEME_DEFS, homeTheme } from '../theme/homeThemes';
import { PHOTO_ACCEPT, PhotoError, preparePhoto } from '../lib/image';
import { apiActive } from '../services/api';
import { Avatar, Btn, Eyebrow, FieldError, FieldLabel, Mono, Photo, Tag, Toggle, inputStyle, textareaStyle } from '../components/ui';
import { CheckIcon, CloseIcon, CopyIcon, ImageIcon, LinkIcon, LockIcon, PaletteIcon, PencilIcon, PlusIcon, ShareIcon, UnitIcon } from '../components/icons';
import { AffiliationChips, PrimarySelect, toggleAffiliation } from '../components/affiliations';
import { orderedSeries } from '../domain/series';
import { EDIT_SECTIONS, type EditSection } from '../state/reducer';
import { REFERRAL_MAX, UNIT_ID_MAX, UNIT_REFERRAL_MESSAGES, checkReferralUrl, checkUnitId, type UnitReferralError } from '../lib/unitReferral';
import { Sheet } from '../components/chrome';

const HEALTHS: Health[] = ['On track', 'Needs help', 'Blocked'];

export function Sheets() {
  const { state: { ui }, act } = useApp();
  const close = useCallback(() => act.closeSheet(), [act]);
  const s = ui.sheet;
  if (!s) return null;
  if (s.kind === 'update') return <UpdateSheet key={s.activityId} activityId={s.activityId} onClose={close} />;
  if (s.kind === 'help') return <HelpSheet activityId={s.activityId} onClose={close} />;
  if (s.kind === 'quickShare') return <QuickShareSheet kitId={s.kitId} onClose={close} />;
  return <ProfileSheet onClose={close} section={s.section} />;
}

function UpdateSheet({ activityId, onClose }: { activityId: string; onClose: () => void }) {
  const { state: { data }, act } = useApp();
  const nowMs = useNow();
  const a = data.activities[activityId];
  const [note, setNote] = useState('');
  const [media, setMedia] = useState(0);
  const [postToPulse, setPostToPulse] = useState(true);
  const [notifyTeam, setNotifyTeam] = useState(true);
  const [tickedNow, setTickedNow] = useState<string[]>([]);
  if (!a) return null;
  const today = ymdIn(nowMs, data.ambassadors[data.me].timeZone);
  const p = progress(data, a.id);
  const mine = tasksOf(data, a.id).filter((t) => t.ownerId === data.me);
  const mates = a.team.filter((m) => m.ambassadorId !== data.me).map((m) => data.ambassadors[m.ambassadorId]?.name.split(' ')[0]).filter(Boolean);

  const publish = () => {
    const doneTitles = mine.filter((t) => t.done && tickedNow.includes(t.id)).map((t) => t.title);
    act.publishProgress({ activityId: a.id, note, mediaCount: media, postToPulse, notifyTeam, doneTitles });
    onClose();
  };

  return (
    <Sheet label="Update progress" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ margin: 0, font: font(700, 24), letterSpacing: '-.03em' }}>Update progress</h2>
        <Mono>{`${a.name} · ${p.total ? `${p.done} / ${p.total} tasks` : 'no tasks yet'}`.toUpperCase()}</Mono>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <FieldLabel>How is it going?</FieldLabel>
        <div role="radiogroup" aria-label="Status" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
          {HEALTHS.map((h) => {
            const on = a.health === h;
            const c = HEALTH_COLORS[h][0];
            return (
              <button key={h} type="button" role="radio" aria-checked={on} onClick={() => act.setHealth(a.id, h)}
                style={{ height: 46, borderRadius: R.pill, border: `1px solid ${on ? c : C.border}`, background: on ? c : C.white, color: on ? C.white : C.ink, font: font(600, 13), transition: 'all .2s' }}>{h}</button>
            );
          })}
        </div>
      </div>
      {mine.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <FieldLabel>Tick what's done</FieldLabel>
          {mine.map((t) => (
            <button key={t.id} type="button" aria-pressed={t.done}
              onClick={() => { act.toggleTask(t.id, { quiet: true }); setTickedNow((x) => (x.includes(t.id) ? x.filter((y) => y !== t.id) : [...x, t.id])); }}
              style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 12, alignItems: 'center', padding: '10px 0', border: 0, borderBottom: `1px solid ${C.divider}`, background: 'transparent', textAlign: 'left', minHeight: 52 }}>
              <span style={{ width: 30, height: 30, borderRadius: 10, border: `2px solid ${t.done ? C.ink : C.violet}`, background: t.done ? C.ink : 'transparent', color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', font: font(800, 14, 1), transition: 'all .2s' }}>{t.done ? '✓' : ''}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ font: font(600, 14), color: t.done ? C.grey : C.ink, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                <span style={{ font: font(500, 11), color: C.grey }}>{t.done ? 'Done' : t.due === today ? 'Due today' : `Due ${dayLabel(t.due)}`}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <FieldLabel htmlFor="u-note">What changed? (optional)</FieldLabel>
        <textarea id="u-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={280} placeholder="Posters printed, 40 flyers out at Covent Garden." style={textareaStyle} />
        <Mono size={10} style={{ alignSelf: 'flex-end' }}>{`${note.length} / 280`}</Mono>
        <label style={{ height: 46, borderRadius: R.input, border: `1px dashed ${media ? C.violet : C.border}`, background: media ? C.soft : C.white, color: media ? C.violet : C.grey, font: font(600, 13), display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <input type="file" accept="image/*,video/*" multiple hidden onChange={(e) => setMedia(Math.min(6, e.target.files?.length ?? 0))} />
          {media ? `${media} attached ✓ · tap to change` : '+ Add photos or video'}
        </label>
      </div>
      <div>
        <Toggle on={postToPulse} onToggle={() => setPostToPulse((x) => !x)} label="Post to UGAIN Pulse" sub={postToPulse ? 'Ambassadors worldwide see it.' : 'Off: team only, saved to the activity timeline.'} />
        <Toggle on={notifyTeam} onToggle={() => setNotifyTeam((x) => !x)} label="Notify the team" sub={mates.length ? mates.join(', ') : 'No teammates yet'} />
      </div>
      <Btn variant="primary" size="lg" full onClick={publish}>{postToPulse ? 'Publish update' : 'Save update'}</Btn>
      <span style={{ font: font(400, 11, 1.4), color: C.grey, textAlign: 'center' }}>Status and progress are visible to UNIT Marketing &amp; Events.</span>
    </Sheet>
  );
}

function HelpSheet({ activityId, onClose }: { activityId: string; onClose: () => void }) {
  const { state: { data }, act } = useApp();
  const a = data.activities[activityId];
  if (!a) return null;
  return (
    <Sheet label="Request help" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ margin: 0, font: font(700, 24), letterSpacing: '-.03em' }}>Request help</h2>
        <Mono>{a.name.toUpperCase()}</Mono>
      </div>
      <p style={{ margin: 0, font: font(400, 13, 1.45), color: C.grey }}>Each request posts to the UGAIN Pulse as Help wanted. Ambassadors tap Offer help; you confirm in Collaboration.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {HELP_ROLES.map((r) => {
          const on = Boolean(data.helpPosted[`${a.id}|${r}`]);
          return <Btn key={r} variant={on ? 'ink' : 'outline'} aria-pressed={on} onClick={() => act.toggleHelpRole(a.id, r)}>{on ? `${r} · posted ✓` : r}</Btn>;
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="ink" size="lg" style={{ flex: 1 }} onClick={onClose}>Done</Btn>
        <Btn size="lg" style={{ flex: 1 }} onClick={() => { onClose(); navigate({ name: 'collab', id: a.id }); }}>Open Collaboration</Btn>
      </div>
    </Sheet>
  );
}

function QuickShareSheet({ kitId, onClose }: { kitId: string; onClose: () => void }) {
  const { state: { data }, act } = useApp();
  const [status, setStatus] = useState('');
  const kit = data.shareKits[kitId];
  if (!kit) return null;
  const asset = kit.assets[0];
  const text = captionWithTags(kit, 'short');
  const url = campaignUrl(kit);
  return (
    <Sheet label="Share" onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0, font: font(700, 24), letterSpacing: '-.03em' }}>Share</h2>
        <Tag label={`APPROVED · ${kit.approvedBy.replace('P2010 ', '').toUpperCase()}`} bg={C.greenBg} fg={C.green} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: 14 }}>
        <span style={{ width: 88, height: 110, borderRadius: R.input, overflow: 'hidden', background: C.night }}><Photo id={asset.photo} pad={6} sizes="88px" /></span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: font(400, 14, 1.4) }}>{kit.captions.short}</span>
          <Mono size={12} color={C.violet}>{kit.hashtags.join(' ')}</Mono>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn size="md" style={{ flex: 1, height: 48 }} onClick={async () => { const ok = await copyText(`${text} ${url}`); setStatus(ok ? 'Caption and link copied. Paste them in your app.' : 'Copy is blocked here. Select the caption above.'); if (ok) act.recordShare(kit.id, 'Copied'); }}>Copy caption</Btn>
        <Btn variant="primary" style={{ flex: 1, height: 48 }} onClick={async () => {
          const r = await nativeShare({ title: kit.label, text, url, imageUrl: photoUrl(asset.photo, 1080) });
          if (r === 'shared') act.recordShare(kit.id, 'Share sheet');
          setStatus(r === 'shared' ? 'Shared from your phone share sheet.' : r === 'cancelled' ? 'Share cancelled.' : 'No share sheet in this browser. Use Copy caption.');
        }}>Share to app</Btn>
      </div>
      <button type="button" onClick={() => { onClose(); navigate({ name: 'share', id: kit.id }); }} style={{ border: 0, background: 'transparent', color: C.violet, font: font(600, 14), padding: '8px 0' }}>Open full share kit · photos, link, platforms ›</button>
      <span role="status" style={{ font: font(400, 12, 1.4), color: C.grey }}>{status || 'Assisted sharing. You publish from your own account.'}</span>
    </Sheet>
  );
}

const SECTIONS: Record<EditSection, { label: string; icon: ReactNode }> = {
  intro: { label: 'Introduction', icon: <PencilIcon /> },
  units: { label: 'UNIT affiliations', icon: <UnitIcon /> },
  photos: { label: 'Photos', icon: <ImageIcon /> },
  appearance: { label: 'Appearance', icon: <PaletteIcon /> },
  referral: { label: 'UNIT ID & referral', icon: <LinkIcon /> },
  privacy: { label: 'Privacy', icon: <LockIcon /> },
};

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * Edit profile: one compact sheet with an icon row; only the chosen section is shown.
 * Introduction, UNIT affiliations and UNIT ID & referral are drafts saved together by the
 * check action. Photos, appearance and privacy switches save as soon as they change (as before).
 * Closing with unsaved drafts asks first.
 */
function ProfileSheet({ onClose, section: initial }: { onClose: () => void; section?: EditSection }) {
  const { state: { data }, act } = useApp();
  const me = data.ambassadors[data.me];
  const catalogue = orderedSeries(data.series);
  const [section, setSection] = useState<EditSection>(initial ?? 'intro');
  const [bio, setBio] = useState(me.bio);
  const [affs, setAffs] = useState({ list: me.affiliations, primary: me.series });
  const [unitId, setUnitId] = useState(me.unitId ?? '');
  const [url, setUrl] = useState(me.referralUrl ?? '');
  const [adding, setAdding] = useState(me.affiliations.length === 0);
  const [err, setErr] = useState<{ bio?: string; units?: string; unitId?: string; url?: string; form?: string }>({});
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const dirty: Partial<Record<EditSection, boolean>> = {
    intro: bio.trim() !== me.bio,
    units: !sameSet(affs.list, me.affiliations) || (affs.list.length > 0 && affs.primary !== me.series),
    referral: unitId.trim() !== (me.unitId ?? '') || url.trim() !== (me.referralUrl ?? ''),
  };
  const anyDirty = Object.values(dirty).some(Boolean);
  // The Sheet's Escape and backdrop call this; a ref keeps its identity stable while typing.
  const dirtyRef = useRef(anyDirty);
  dirtyRef.current = anyDirty;
  const requestClose = useCallback(() => (dirtyRef.current ? setConfirming(true) : onClose()), [onClose]);

  const tabs = useRef<Partial<Record<EditSection, HTMLButtonElement | null>>>({});
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = EDIT_SECTIONS.indexOf(section);
    const to = e.key === 'ArrowRight' ? (i + 1) % EDIT_SECTIONS.length : e.key === 'ArrowLeft' ? (i + EDIT_SECTIONS.length - 1) % EDIT_SECTIONS.length
      : e.key === 'Home' ? 0 : e.key === 'End' ? EDIT_SECTIONS.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    setSection(EDIT_SECTIONS[to]);
    tabs.current[EDIT_SECTIONS[to]]?.focus();
  };

  const save = async () => {
    if (saving) return;
    if (!anyDirty) return onClose();
    const msg = (e: UnitReferralError | null) => (e ? UNIT_REFERRAL_MESSAGES[e] : undefined);
    if (dirty.referral) {
      const e = { unitId: msg(checkUnitId(unitId)), url: msg(checkReferralUrl(url)) };
      if (e.unitId || e.url) {
        setErr(e);
        return setSection('referral');
      }
    }
    setErr({});
    setSaving(true);
    const r = await act.saveProfile({
      bio: dirty.intro ? bio : undefined,
      affiliations: dirty.units ? affs : undefined,
      unit: dirty.referral ? { unitId, referralUrl: url } : undefined,
    });
    setSaving(false);
    if (r.ok) return onClose();
    // Drafts stay in place; point at the section the server rejected.
    if (r.code === 'referral_host_not_allowed') return (setErr({ url: UNIT_REFERRAL_MESSAGES.url_host_not_allowed }), setSection('referral'));
    if (r.field === 'unitId') return (setErr({ unitId: UNIT_REFERRAL_MESSAGES.unit_id_invalid }), setSection('referral'));
    if (r.field === 'referralUrl') return (setErr({ url: UNIT_REFERRAL_MESSAGES.url_invalid }), setSection('referral'));
    if (r.field === 'bio') return (setErr({ bio: 'This introduction couldn’t be saved. Shorten it and try again.' }), setSection('intro'));
    if (r.field === 'affiliations' || r.field === 'primary') return (setErr({ units: 'One of these UNITs isn’t available. Remove it and try again.' }), setSection('units'));
    setErr({ form: r.code === 'network' ? 'Couldn’t reach UGAIN. Your changes are kept; try again.' : 'Couldn’t save. Your changes are kept; try again.' });
  };

  const iconBtn: CSSProperties = { width: 44, height: 44, border: 0, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
  const selectedDefs = affs.list.map((id) => catalogue.find((s) => s.id === id) ?? { id, label: `${id} UNITS`, referenceName: id, order: 999, status: 'upcoming' as const });
  const toggle = (id: string) => setAffs((a) => toggleAffiliation(a.list, a.primary, id));
  const copy = async (text: string, what: string) => act.toast((await copyText(text)) ? `${what} copied` : 'Could not copy. Press and hold to copy it.');

  return (
    <Sheet label="Edit profile" onClose={requestClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 44px', alignItems: 'center', margin: '-6px -10px 0' }}>
        <button type="button" className="press" aria-label="Close" onClick={requestClose} style={{ ...iconBtn, background: 'transparent', color: C.ink }}><CloseIcon size={22} /></button>
        <h2 style={{ margin: 0, textAlign: 'center', font: font(700, 17), letterSpacing: '-.01em' }}>Edit profile</h2>
        <button type="button" className="press" aria-label={saving ? 'Saving' : 'Save changes'} aria-busy={saving} disabled={saving} onClick={() => void save()}
          style={{ ...iconBtn, background: anyDirty ? C.violet : 'transparent', color: anyDirty ? C.white : C.violet, opacity: saving ? 0.6 : 1, transition: 'background .2s, color .2s' }}>
          <CheckIcon size={22} />
        </button>
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Unsaved changes" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '8px 8px 8px 14px', borderRadius: R.panel, background: C.amberBg, border: `1px solid ${C.amberBorder}` }}>
          <span style={{ font: font(600, 13), color: C.ink }}>Discard unsaved changes?</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <Btn size="md" autoFocus onClick={() => setConfirming(false)}>Keep editing</Btn>
            <Btn size="md" variant="ink" onClick={onClose}>Discard</Btn>
          </span>
        </div>
      )}
      {err.form && <FieldError>{err.form}</FieldError>}

      <div role="tablist" aria-label="Profile sections" style={{ display: 'grid', gridTemplateColumns: `repeat(${EDIT_SECTIONS.length}, minmax(0, 1fr))`, padding: 3, borderRadius: R.panel, background: C.paper, border: `1px solid ${C.subtle}` }}>
        {EDIT_SECTIONS.map((s) => {
          const on = s === section;
          return (
            <button key={s} ref={(el) => { tabs.current[s] = el; }} type="button" role="tab" id={`pe-tab-${s}`} aria-selected={on} aria-controls="pe-panel" tabIndex={on ? 0 : -1}
              aria-label={`${SECTIONS[s].label}${dirty[s] ? ', unsaved changes' : ''}`} title={SECTIONS[s].label}
              onClick={() => setSection(s)} onKeyDown={onTabKey} className="press"
              style={{ position: 'relative', height: 44, minWidth: 0, border: 0, borderRadius: R.input, background: on ? C.soft : 'transparent', color: on ? C.violet : C.ink, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, transition: 'background .2s, color .2s' }}>
              {SECTIONS[s].icon}
              {dirty[s] && <span aria-hidden="true" style={{ position: 'absolute', top: 7, right: 'calc(50% - 15px)', width: 6, height: 6, borderRadius: '50%', background: C.violet }} />}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id="pe-panel" aria-labelledby={`pe-tab-${section}`} style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 150 }}>
        <span style={{ font: font(600, 12), color: C.grey, letterSpacing: '.02em' }}>{SECTIONS[section].label}</span>

        {section === 'intro' && (
          <>
            <textarea aria-label="Introduction" value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={300} aria-invalid={Boolean(err.bio)}
              style={{ ...textareaStyle, minHeight: 'calc(4 * 1.4em + 26px)', overflowY: 'auto', scrollMarginBottom: 96 }} />
            <Mono size={10} style={{ alignSelf: 'flex-end' }}>{`${bio.length} / 300`}</Mono>
            {err.bio && <FieldError>{err.bio}</FieldError>}
          </>
        )}

        {section === 'units' && (
          <>
            {affs.list.length > 0
              ? <AffiliationChips label="Your UNITs (tap to remove)" items={selectedDefs} selected={affs.list} primary={affs.primary} onToggle={toggle} />
              : <span style={{ font: font(400, 13), color: C.grey }}>No UNITs yet.</span>}
            <PrimarySelect catalogue={catalogue} selected={affs.list} primary={affs.primary} onChange={(primary) => setAffs((a) => ({ ...a, primary }))} />
            {!adding && (
              <Btn size="md" style={{ alignSelf: 'flex-start', height: 44 }} aria-expanded={false} onClick={() => setAdding(true)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><PlusIcon size={16} />Add UNIT</span>
              </Btn>
            )}
            {adding && <AffiliationChips label="Add a UNIT" items={catalogue.filter((s) => !affs.list.includes(s.id))} selected={[]} onToggle={toggle} />}
            {err.units && <FieldError>{err.units}</FieldError>}
          </>
        )}

        {section === 'photos' && (
          <>
            <PhotoField kind="avatar" label="Profile photo" url={me.photoUrl}
              preview={(src) => (src ? <img src={src} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} /> : <Avatar person={me} size={56} />)} />
            <PhotoField kind="cover" label="Cover photo" url={me.coverUrl}
              preview={(src) => <span style={{ width: 84, height: 56, borderRadius: 12, overflow: 'hidden', background: C.night, display: 'block' }}>{src && <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</span>} />
          </>
        )}

        {section === 'appearance' && (
          <>
            <div role="radiogroup" aria-label="Homepage colour (only you see it)" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              {HOME_THEME_DEFS.map((t) => {
                const on = homeTheme(data.appearance?.theme).id === t.id;
                return (
                  <button key={t.id} type="button" role="radio" aria-checked={on} onClick={() => act.setHomeTheme(t.id)} className="press"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48, padding: '8px 12px', borderRadius: R.panel, border: `${on ? 2 : 1}px solid ${on ? C.violet : C.subtle}`, background: C.white, color: C.ink, font: font(600, 13), textAlign: 'left' }}>
                    <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: '50%', background: t.bg, border: `1px solid ${C.border}`, flex: 'none' }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <PhotoField kind="background" label="Background image" url={data.appearance?.backgroundUrl}
              preview={(src) => <span style={{ width: 84, height: 56, borderRadius: 12, overflow: 'hidden', background: homeTheme(data.appearance?.theme).bg, border: `1px solid ${C.subtle}`, display: 'block' }}>{src && <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</span>} />
          </>
        )}

        {section === 'referral' && (
          <>
            <CopyField id="unit-id" label="UNIT ID" value={unitId} onChange={setUnitId} saved={me.unitId} error={err.unitId} maxLength={UNIT_ID_MAX + 8} onCopy={(v) => void copy(v, 'UNIT ID')} />
            <CopyField id="unit-ref" label="Referral link" type="url" placeholder="https://" value={url} onChange={setUrl} saved={me.referralUrl} error={err.url} maxLength={REFERRAL_MAX + 8} onCopy={(v) => void copy(v, 'Referral link')}
              onShare={me.referralUrl ? async () => {
                const r = await nativeShare({ title: 'UNIT referral link', text: 'Join me on UNIT', url: me.referralUrl! });
                if (r === 'unsupported' || r === 'failed') await copy(me.referralUrl!, 'Referral link');
              } : undefined} />
            <button type="button" onClick={() => setSection('privacy')} style={{ alignSelf: 'flex-start', minHeight: 44, border: 0, padding: 0, background: 'transparent', font: font(500, 12), color: C.grey, textAlign: 'left' }}>
              {data.privacy.showUnitReferral ? 'Shown where your profile is visible' : 'Only you can see these'} · <span style={{ color: C.violet }}>Privacy</span>
            </button>
          </>
        )}

        {section === 'privacy' && (
          <div>
            <Toggle on={data.privacy.publicProfile} onToggle={() => act.setPrivacy('publicProfile', !data.privacy.publicProfile)} label="Public profile" sub="Off: only your activity teams see your story." />
            <Toggle on={data.privacy.showCity} onToggle={() => act.setPrivacy('showCity', !data.privacy.showCity)} label="Show my city" />
            <Toggle on={data.privacy.showSocials} onToggle={() => act.setPrivacy('showSocials', !data.privacy.showSocials)} label="Show my social links" sub="Email and phone are never shown." />
            <Toggle on={Boolean(data.privacy.showUnitReferral)} onToggle={() => act.setPrivacy('showUnitReferral', !data.privacy.showUnitReferral)} label="Show UNIT ID & referral" sub="Where your profile is visible." />
          </div>
        )}
      </div>
    </Sheet>
  );
}

/** A labelled text field; when a saved value exists, small Copy (and Share) icons act on the saved value. */
function CopyField({ id, label, value, onChange, saved, error, onCopy, onShare, type, placeholder, maxLength }: {
  id: string; label: string; value: string; onChange: (v: string) => void; saved?: string; error?: string;
  onCopy: (v: string) => void; onShare?: () => void; type?: 'url'; placeholder?: string; maxLength: number;
}) {
  const small: CSSProperties = { width: 44, height: 44, flex: 'none', border: 0, borderRadius: '50%', background: 'transparent', color: C.grey, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input id={id} type={type ?? 'text'} inputMode={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength}
          autoComplete="off" autoCapitalize="off" spellCheck={false} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-err` : undefined}
          style={{ ...inputStyle, height: 46, flex: 1, minWidth: 0 }} />
        {saved && <button type="button" className="press" aria-label={`Copy ${label}`} title={`Copy ${label}`} onClick={() => onCopy(saved)} style={small}><CopyIcon size={18} /></button>}
        {saved && onShare && <button type="button" className="press" aria-label={`Share ${label}`} title={`Share ${label}`} onClick={onShare} style={small}><ShareIcon size={18} /></button>}
      </div>
      {error && <span id={`${id}-err`}><FieldError>{error}</FieldError></span>}
    </div>
  );
}

const CameraGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);

/** One photo slot: preview, choose/replace (camera), remove. Saved to the server immediately. */
function PhotoField({ kind, label, url, preview }: { kind: PhotoKind; label: string; url?: string; preview: (src?: string) => ReactNode }) {
  const { act } = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [local, setLocal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => () => { if (local) URL.revokeObjectURL(local); }, [local]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setMsg('');
    setBusy(true);
    try {
      const blob = await preparePhoto(file, kind);
      setLocal(URL.createObjectURL(blob)); // preview while it uploads
      const ok = await act.savePhoto(kind, blob);
      setLocal(null);
      if (!ok) setMsg('Not saved.');
    } catch (err) {
      setLocal(null);
      setMsg(err instanceof PhotoError ? err.message : 'That photo couldn’t be prepared.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const remove = async () => {
    setBusy(true);
    await act.removePhoto(kind);
    setBusy(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ position: 'relative', flex: 'none', opacity: busy ? 0.6 : 1 }}>{preview(local ?? url)}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <Eyebrow>{label}</Eyebrow>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input ref={input} type="file" accept={PHOTO_ACCEPT} hidden onChange={(e) => void pick(e.target.files?.[0])} />
          <Btn size="md" disabled={busy || !apiActive} onClick={() => input.current?.click()} aria-label={`${url ? 'Replace' : 'Add'} ${label.toLowerCase()}`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><CameraGlyph />{busy ? 'Saving…' : url ? 'Replace' : 'Add'}</span>
          </Btn>
          {url && <Btn size="md" disabled={busy} onClick={() => void remove()} aria-label={`Remove ${label.toLowerCase()}`}>Remove</Btn>}
        </div>
        {!apiActive && <Mono size={10}>Sign in with Google to add photos.</Mono>}
        {msg && <span role="alert" style={{ font: font(400, 12, 1.4), color: C.red }}>{msg}</span>}
      </div>
    </div>
  );
}
